/**
 * A reusable accordion component with a header and collapsible content.
 *
 * ## Attributes
 * - `header`: The text to display in the accordion header
 * - `open`: When present, the accordion is expanded
 * - `help-tooltip-default-text`: Default text for help tooltip
 * - `help-tooltip-dict-key`: Dictionary key for localized help tooltip
 * - `help-placement`: Placement of the help tooltip (default: "right")
 *
 * ## Slots
 * - (default): Accordion body content
 * - `help`: An `<as-hovercard>` element to use as the header help popover
 * - `header-start`: Content inserted before the header text (e.g. a leading icon or swatch)
 * - `header-end`: Content inserted after the spacer, before the arrow (e.g. a badge or count)
 *
 * ## Events
 * - `toggle`: Dispatched when the accordion is opened or closed
 *   - Detail: `{ open: boolean }`
 *
 * ## Usage
 * ```html
 * <as-accordion header="Section Title" open>
 *   <p>Content goes here</p>
 * </as-accordion>
 *
 * <!-- With leading icon and trailing badge -->
 * <as-accordion header="Files">
 *   <span slot="header-start">📁</span>
 *   <span slot="header-end">3</span>
 *   <p>Content</p>
 * </as-accordion>
 *
 * <!-- With help tooltip -->
 * <as-accordion header="Section" help-tooltip-default-text="Help text here">
 *   <p>Content</p>
 * </as-accordion>
 *
 * <!-- With hovercard -->
 * <as-accordion header="Section">
 *   <as-hovercard slot="help">Detailed help content</as-hovercard>
 *   <p>Content</p>
 * </as-accordion>
 * ```
 */
import '../overlays/as-hovercard-wrapper.ts';
import '../overlays/as-tooltip-wrapper.ts';
import styleText from './as-accordion.css?raw';
import flashStyle from '../../styles/flash.css?raw';

export class AsAccordion extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private static readonly flashSheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(flashStyle);
    return sheet;
  })();

  private _helpTooltipDefaultText: string = '';
  private _helpTooltipDictKey: string = '';

  static get observedAttributes(): string[] {
    return ['header', 'open', 'help-tooltip-default-text', 'help-tooltip-dict-key', 'help-placement'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsAccordion.sheet, AsAccordion.flashSheet];
    this.render();
  }

  private render(): void {
    const headerText = this.getAttribute('header') ?? 'Section';

    this.shadowRoot!.innerHTML = /*html*/`
      <div class="accordion-container">
        <div class="accordion-header">
          <slot name="header-start"></slot>
          <span class="header-text">${headerText}</span>
          <span class="header-help"></span>
          <span class="header-spacer"></span>
          <slot name="header-end"></slot>
          <span class="arrow"></span>
        </div>
        <div class="accordion-content">
          <div class="accordion-content-inner">
            <slot></slot>
          </div>
        </div>
      </div>
      <slot name="help" style="display:none;"></slot>
    `;

    this.setupEventListeners();
    this.updateHelpIcon();
  }

  private isClickFromHeaderSlot(e: Event): boolean {
    const path = e.composedPath();
    for (const slotName of ['header-start', 'header-end']) {
      const slot = this.shadowRoot?.querySelector(`slot[name="${slotName}"]`) as HTMLSlotElement | null;
      if (!slot) continue;
      for (const assigned of slot.assignedElements({ flatten: true })) {
        if (path.some(node => node === assigned || (node instanceof Node && assigned.contains(node)))) {
          return true;
        }
      }
    }
    return false;
  }

  private setupEventListeners(): void {
    const header = this.shadowRoot?.querySelector('.accordion-header');
    header?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.header-help')) return;
      if (this.isClickFromHeaderSlot(e)) return;
      this.toggle();
    });

    // Listen for slotchange on help slot
    const helpSlot = this.shadowRoot?.querySelector('slot[name="help"]') as HTMLSlotElement;
    helpSlot?.addEventListener('slotchange', () => this.updateHelpIcon());
  }

  private updateHelpIcon(): void {
    const helpContainer = this.shadowRoot?.querySelector('.header-help') as HTMLElement;
    if (!helpContainer) return;

    const placement = this.getAttribute('help-placement') ?? 'right';

    // Check for slotted hovercard
    const helpSlot = this.shadowRoot?.querySelector('slot[name="help"]') as HTMLSlotElement;
    const slottedHovercard = helpSlot?.assignedElements().find(
      el => el.tagName.toLowerCase() === 'as-hovercard'
    ) as HTMLElement | null;

    const hasTooltip = !!(this._helpTooltipDictKey || this._helpTooltipDefaultText);
    const hasHovercard = !!slottedHovercard;

    if (hasHovercard) {
      // Clone the hovercard into the wrapper
      const hovercardClone = slottedHovercard.cloneNode(true) as HTMLElement;
      // The close button is opt-in via show-close; strip it so the hover-help
      // card never renders one. The original may already have inserted its
      // button into the light DOM, so drop the cloned (listener-less) node too.
      hovercardClone.removeAttribute('show-close');
      hovercardClone.querySelector('.as-hovercard-close')?.remove();
      hovercardClone.removeAttribute('slot');

      helpContainer.innerHTML = /*html*/`
        <as-hovercard-wrapper placement="${placement}" show-on-hover>
          <span class="help-icon">?</span>
        </as-hovercard-wrapper>
      `;

      const wrapper = helpContainer.querySelector('as-hovercard-wrapper');
      wrapper?.appendChild(hovercardClone);
    } else if (hasTooltip) {
      const attrs: string[] = [];
      if (this._helpTooltipDictKey) {
        attrs.push(`dict-key="${this._helpTooltipDictKey}"`);
      }
      if (this._helpTooltipDefaultText) {
        attrs.push(`default-text="${this._helpTooltipDefaultText}"`);
      }

      helpContainer.innerHTML = /*html*/`
        <as-tooltip-wrapper placement="${placement}" show-on-hover ${attrs.join(' ')}>
          <span class="help-icon">?</span>
        </as-tooltip-wrapper>
      `;
    } else {
      helpContainer.innerHTML = '';
    }
  }

  public toggle(): void {
    if (this.hasAttribute('open')) {
      this.removeAttribute('open');
    } else {
      this.setAttribute('open', '');
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'open':
        this.dispatchEvent(new CustomEvent('toggle', {
          detail: { open: this.hasAttribute('open') },
          bubbles: true,
          composed: true
        }));
        break;
      case 'header':
        const headerEl = this.shadowRoot?.querySelector('.header-text');
        if (headerEl) {
          headerEl.textContent = newValue ?? 'Section';
        }
        break;
      case 'help-tooltip-default-text':
        this._helpTooltipDefaultText = newValue ?? '';
        this.updateHelpIcon();
        break;
      case 'help-tooltip-dict-key':
        this._helpTooltipDictKey = newValue ?? '';
        this.updateHelpIcon();
        break;
      case 'help-placement':
        this.updateHelpIcon();
        break;
    }
  }

  get open(): boolean {
    return this.hasAttribute('open');
  }

  set open(value: boolean) {
    if (value) {
      this.setAttribute('open', '');
    } else {
      this.removeAttribute('open');
    }
  }

  get header(): string {
    return this.getAttribute('header') ?? 'Section';
  }

  set header(value: string) {
    this.setAttribute('header', value);
  }
}

if (!customElements.get('as-accordion')) {
  customElements.define('as-accordion', AsAccordion);
} else {
  console.warn('as-accordion already defined');
}
